import { computed, inject, onMounted } from 'vue';

// COMPOSABLES
import { useQueryURL } from '@/composables/useQueryURL';

// COMPONENTS
import SuccessTransaction from '../SuccessTransaction/index.vue';
import SuccessPreTransaction from '../SuccessPreTransaction/index.vue';

//CONFIG
import { transactionProviderKey as transactionProvider,
  initialValuesTransactionProvider,
} from '../../constants/transactionProvider.constant';

// STORE
import type { SuccessType } from '../../types/success.type';
import type {
  IProductCard,
  ProductCardType,
} from '@/helpers/constants/productCards';
import { useProductCards } from '@/composables/useProductCards';
import { useWalletStore } from '@/stores/wallet';

export function useSuccess() {
  const {
    isCCA,
    isP2P,
    isComexFlow,
    preTransactionResponse,
    originRoute,
  } = inject(transactionProvider, initialValuesTransactionProvider);

  const { getBannerProducts } = useProductCards();

  const storeWallet = useWalletStore();

  const isCompensationAccount = computed(() => {
    const walletOrigin = storeWallet.wallets.find(
      (wallet) => wallet.balance.currency === originRoute.value?.currency?.code
    );
    const isCompensationWallet =
      !!walletOrigin?.compensationNumbers?.length &&
      walletOrigin?.compensationNumbers?.some((el) => el.active);

    return isCompensationWallet;
  });

  const successType = computed((): SuccessType => {
    let type: SuccessType = 'TRANSACTION';

    if(isComexFlow.value) {
      type = isP2P.value ? 'P2P_COMEX' : 'TRANSACTION_COMEX';
    }

    if (isCCA.value) type = 'CCA';

    return type;
  });

  const successComponent = computed(() => {
    if (preTransactionResponse.value?.type === 'PRE_TRANSACTION') {
      return SuccessPreTransaction;
    }
    return SuccessTransaction;
  });

  const showItems = computed((): IProductCard[] => {
    const transferType: Record<SuccessType | 'DEFAULT', ProductCardType[]> = {
      TRANSACTION: ['EXCHANGE', 'P2P'],
      P2P: ['CARD', 'EXCHANGE', 'REMITTANCE'],
      TRANSACTION_COMEX: ['COMEX', 'P2P'],
      P2P_COMEX: ['COMEX', 'REMITTANCE'],
      CCA: ['P2P', 'REMITTANCE'],
      DEFAULT: [],
    };

    const showProducts = transferType[successType.value];

    if (isCompensationAccount.value) {
      showProducts.push('COMEX_COMPENSATION');
    }

    return getBannerProducts({ include: showProducts });
  });

  onMounted(async () => {
    if (isP2P.value) {
      useQueryURL({ transaction: 'success', type: 'p2p' });
    } else useQueryURL({ transaction: 'success', type: 'banking' });
  });

  return {
    successType,
    successComponent,
    showItems,
  };
}
